import {
  useCancelMultiFileUpload,
  useCancelSingleFileUpload,
} from "@/components/common/file-upload-context/use-cancel-upload";
import { dismissBackgroundTask } from "@/store/background-tasks/background-tasks-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  FileUploadTask,
  isMultiFileUploadTask,
  MultiFileUploadTask,
} from "@/utils/background-tasks";
import {
  NoTranslate,
  red,
  RemainingTimeLabel,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
} from "@faro-lotv/service-wires";
import { useCallback, useMemo, type JSX } from "react";
import { CardError } from "../common/card-error";
import { CardProgress } from "../common/card-progress";
import { CardAction, GenericCardLayout } from "../layouts/generic-card-layout";

import {
  getTaskStatusLabel,
  getTaskTypeDescription,
} from "../task-descriptions";

type UploadTaskCardProps = {
  /** The task to track */
  task: FileUploadTask | MultiFileUploadTask;
};

/** @returns the upload menu card to track a PointCloud upload */
export function UploadTaskCard({ task }: UploadTaskCardProps): JSX.Element {
  const dispatch = useAppDispatch();
  const cancelSingleFileUpload = useCancelSingleFileUpload();
  const cancelMultiFileUpload = useCancelMultiFileUpload();
  const isMultiFileUpload = isMultiFileUploadTask(task);

  const dismissErrorTask = useCallback((): void => {
    dispatch(dismissBackgroundTask(task.id));
  }, [dispatch, task.id]);

  const menu = useMemo<CardAction[]>(() => {
    if (task.state === BackgroundTaskState.failed) {
      return [{ name: "Clear", callback: dismissErrorTask }];
    }
    if (isBackgroundTaskActive(task.state)) {
      return [
        {
          name: "Cancel import",
          callback: () =>
            isMultiFileUpload
              ? cancelMultiFileUpload(task.id)
              : cancelSingleFileUpload(task.id),
          color: red[500],
        },
      ];
    }
    return [];
  }, [
    task.state,
    task.id,
    dismissErrorTask,
    isMultiFileUpload,
    cancelMultiFileUpload,
    cancelSingleFileUpload,
  ]);

  return (
    <GenericCardLayout
      name={
        isMultiFileUpload ? (
          <>
            Processing{" "}
            <TranslateVar name="fileCount">
              {task.metadata.filenames.length}
            </TranslateVar>{" "}
            files
          </>
        ) : (
          <NoTranslate>{task.metadata.filename}</NoTranslate>
        )
      }
      subText={getTaskTypeDescription(task)}
      startTime={task.createdAt}
      menu={menu}
    >
      {isBackgroundTaskActive(task.state) && (
        <CardProgress
          label={getTaskStatusLabel(task)}
          subLabel={
            task.expectedEnd && (
              <NoTranslate>
                <RemainingTimeLabel expectedEnd={task.expectedEnd} />
              </NoTranslate>
            )
          }
          task={task}
        />
      )}
      {task.state === BackgroundTaskState.failed && <CardError task={task} />}
    </GenericCardLayout>
  );
}
